import React from "react";
import { Search, Filter, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

export interface DeviceFilterState {
  search: string;
  sortBy: "name" | "recent" | "location" | "alerts";
  sortDirection: "asc" | "desc";
  showAlertsOnly: boolean;
  showOfflineOnly: boolean;
  showDemoOnly: boolean;
  groupName: string;
}

interface DeviceFiltersProps {
  filters: DeviceFilterState;
  onFiltersChange: (filters: DeviceFilterState) => void;
  totalDevices: number;
  filteredCount: number;
  availableGroups: string[];
}

export const DeviceFilters: React.FC<DeviceFiltersProps> = ({
  filters,
  onFiltersChange,
  totalDevices,
  filteredCount,
  availableGroups,
}) => {
  const hasActiveFilters = 
    filters.search || 
    filters.sortBy !== "recent" || 
    filters.showAlertsOnly || 
    filters.showOfflineOnly ||
    filters.showDemoOnly ||
    filters.groupName !== "";

  const clearFilters = () => {
    onFiltersChange({
      search: "",
      sortBy: "recent",
      sortDirection: "desc",
      showAlertsOnly: false,
      showOfflineOnly: false,
      showDemoOnly: false,
      groupName: "",
    });
  };

  return (
    <div className="brand-device-filters flex flex-col sm:flex-row gap-4 p-4 bg-component-background border rounded-lg mb-6">
      {/* Search Input */}
      <div className="flex-1 relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by device, serial number, description, or organisation..."
          value={filters.search}
          onChange={(e) =>
            onFiltersChange({ ...filters, search: e.target.value })
          }
          className="pl-10"
        />
      </div>

      {/* Group Filter */}
      {availableGroups.length > 0 && (
        <Select
          value={filters.groupName || "all"}
          onValueChange={(value) =>
            onFiltersChange({ ...filters, groupName: value === "all" ? "" : value })
          }
        >
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="All Groups" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Groups</SelectItem>
            {availableGroups.map((group) => (
              <SelectItem key={group} value={group}>
                {group}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {/* Sort & Filter Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90">
            <Filter className="h-4 w-4" />
            Sort & Filter
            {hasActiveFilters && (
              <Badge variant="secondary" className="ml-1 px-1.5 py-0.5 text-xs">
                {[
                  filters.search && "search",
                  filters.sortBy !== "recent" && "sort",
                  filters.showAlertsOnly && "alerts",
                  filters.showOfflineOnly && "offline",
                  filters.showDemoOnly && "demo",
                  filters.groupName && "group",
                ].filter(Boolean).length}
              </Badge>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-48">
          <DropdownMenuLabel>Sort by</DropdownMenuLabel>
          <DropdownMenuRadioGroup
            value={filters.sortBy}
            onValueChange={(value) =>
              onFiltersChange({
                ...filters,
                sortBy: value as DeviceFilterState["sortBy"],
              })
            }
          >
            <DropdownMenuRadioItem value="recent">
              Most Recent Images
            </DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="name">Name</DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="alerts">Alerts First</DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="location">Location</DropdownMenuRadioItem>
          </DropdownMenuRadioGroup>

          <DropdownMenuSeparator />
          
          <DropdownMenuLabel>Filter</DropdownMenuLabel>
          <div className="px-2 py-1 space-y-2">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.showAlertsOnly}
                onChange={(e) =>
                  onFiltersChange({
                    ...filters,
                    showAlertsOnly: e.target.checked,
                  })
                }
                className="rounded"
              />
              <span className="text-sm">Alerts only</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.showOfflineOnly}
                onChange={(e) =>
                  onFiltersChange({
                    ...filters,
                    showOfflineOnly: e.target.checked,
                  })
                }
                className="rounded"
              />
              <span className="text-sm">Offline only</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.showDemoOnly}
                onChange={(e) =>
                  onFiltersChange({
                    ...filters,
                    showDemoOnly: e.target.checked,
                  })
                }
                className="rounded"
              />
              <span className="text-sm">Demo only</span>
            </label>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Clear Filters */}
      {hasActiveFilters && (
        <Button variant="ghost" onClick={clearFilters} className="gap-2">
          <X className="h-4 w-4" />
          Clear
        </Button>
      )}

      {/* Results Count */}
      <div className="flex items-center text-sm text-muted-foreground">
        {filteredCount} of {totalDevices} devices
      </div>
    </div>
  );
};